import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  FiFileText, FiDownload, FiEye, FiEdit, FiPlus, FiCheck, 
  FiX, FiClock, FiUser, FiCalendar, FiDollarSign, FiMail,
  FiRefreshCw, FiSearch, FiFilter
} from 'react-icons/fi';
import { formatCurrency, formatDate } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContractsPage() {
  const [contracts, setContracts] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContract, setSelectedContract] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Load contracts
      const contractsRes = await fetch(`${API_URL}/api/contracts/`, { headers });
      if (contractsRes.ok) {
        const data = await contractsRes.json();
        setContracts(data);
      }

      // Load reservations without contracts
      const reservationsRes = await fetch(`${API_URL}/api/reservations`, { headers });
      if (reservationsRes.ok) {
        const data = await reservationsRes.json();
        // Filter reservations that need contracts
        const needsContract = data.filter(r => 
          r.contract_required && !r.contract_signed && r.status !== 'cancelled'
        );
        setReservations(needsContract);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const createContract = async (reservationId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/contracts/create-from-reservation/${reservationId}`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const contract = await response.json();
        toast.success('Contrat créé avec succès');
        loadData();
        return contract;
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la création du contrat');
      }
    } catch (error) {
      console.error('Error creating contract:', error);
      toast.error('Erreur lors de la création du contrat');
    }
  };

  const sendContractByEmail = async (contractId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/contracts/${contractId}/send-email`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Contrat envoyé par email');
      } else {
        toast.error('Erreur lors de l\'envoi');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    }
  };

  const downloadContract = async (contractId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/contracts/${contractId}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `contrat-${contractId}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        toast.error('Erreur lors du téléchargement');
      }
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const getStatusBadge = (contract) => {
    if (contract.signed_at) {
      return <Badge className="bg-green-100 text-green-700">Signé</Badge>;
    }
    if (contract.sent_at && !contract.signed_at) {
      return <Badge className="bg-blue-100 text-blue-700">En attente de signature</Badge>;
    }
    return <Badge className="bg-amber-100 text-amber-700">Brouillon</Badge>;
  };

  const filteredContracts = contracts.filter(contract => {
    const matchesSearch = 
      contract.client_info?.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contract.client_info?.last_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contract.contract_number?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' ||
      (statusFilter === 'signed' && contract.signed_at) ||
      (statusFilter === 'pending' && contract.sent_at && !contract.signed_at) ||
      (statusFilter === 'draft' && !contract.sent_at);
    
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Contrats de Location</h1>
          <p className="text-slate-500">Gérez vos contrats et CGV</p>
        </div>
        <div className="flex gap-2">
          <Link to="/dashboard/contracts/template">
            <Button variant="outline" className="gap-2">
              <FiEdit className="w-4 h-4" />
              Modèle CGV
            </Button>
          </Link>
          <Button onClick={loadData} variant="outline" className="gap-2">
            <FiRefreshCw className="w-4 h-4" />
            Actualiser
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <FiFileText className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{contracts.length}</p>
                <p className="text-sm text-slate-500">Total contrats</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <FiCheck className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{contracts.filter(c => c.signed_at).length}</p>
                <p className="text-sm text-slate-500">Signés</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <FiClock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{contracts.filter(c => c.sent_at && !c.signed_at).length}</p>
                <p className="text-sm text-slate-500">En attente</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <FiX className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{reservations.length}</p>
                <p className="text-sm text-slate-500">Sans contrat</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Reservations needing contracts */}
      {reservations.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader>
            <CardTitle className="text-amber-800 flex items-center gap-2">
              <FiClock className="w-5 h-5" />
              Réservations en attente de contrat
            </CardTitle>
            <CardDescription className="text-amber-700">
              Ces réservations nécessitent un contrat de location
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {reservations.map(res => (
                <div key={res.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200">
                  <div className="flex items-center gap-4">
                    <div>
                      <p className="font-medium text-slate-900">{res.client_name}</p>
                      <p className="text-sm text-slate-500">
                        {res.product_name} • {formatDate(res.start_date)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-semibold text-slate-900">
                      {formatCurrency(res.total_price || res.total_cost)}
                    </span>
                    <Button 
                      size="sm"
                      onClick={() => createContract(res.id)}
                      className="gap-1"
                    >
                      <FiPlus className="w-4 h-4" />
                      Créer contrat
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Rechercher un contrat..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex gap-2">
          {['all', 'draft', 'pending', 'signed'].map(status => (
            <Button
              key={status}
              variant={statusFilter === status ? 'default' : 'outline'}
              size="sm"
              onClick={() => setStatusFilter(status)}
            >
              {status === 'all' && 'Tous'}
              {status === 'draft' && 'Brouillons'}
              {status === 'pending' && 'En attente'}
              {status === 'signed' && 'Signés'}
            </Button>
          ))}
        </div>
      </div>

      {/* Contracts List */}
      <div className="space-y-4">
        {filteredContracts.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <FiFileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">Aucun contrat</h3>
              <p className="text-slate-500 mb-4">
                {searchQuery || statusFilter !== 'all' 
                  ? 'Aucun contrat ne correspond à vos critères'
                  : 'Créez un contrat à partir d\'une réservation'}
              </p>
            </CardContent>
          </Card>
        ) : (
          filteredContracts.map(contract => (
            <motion.div
              key={contract.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="hover:shadow-md transition-shadow">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1">
                      <div className="p-3 bg-slate-100 rounded-lg">
                        <FiFileText className="w-6 h-6 text-slate-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-slate-900">
                            Contrat #{contract.contract_number}
                          </h3>
                          {getStatusBadge(contract)}
                        </div>
                        <p className="text-slate-600">
                          {contract.client_info?.first_name} {contract.client_info?.last_name}
                        </p>
                        <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <FiCalendar className="w-4 h-4" />
                            {formatDate(contract.service_info?.event_date)}
                          </span>
                          <span className="flex items-center gap-1">
                            <FiDollarSign className="w-4 h-4" />
                            {formatCurrency(contract.financials?.total_ttc)}
                          </span>
                        </div>
                        {contract.signed_at && (
                          <p className="text-xs text-green-600 mt-2">
                            Signé le {formatDate(contract.signed_at)}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelectedContract(contract)}
                      >
                        <FiEye className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => downloadContract(contract.id)}
                      >
                        <FiDownload className="w-4 h-4" />
                      </Button>
                      {!contract.signed_at && (
                        <Button
                          size="sm"
                          onClick={() => sendContractByEmail(contract.id)}
                          className="gap-1"
                        >
                          <FiMail className="w-4 h-4" />
                          Envoyer
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))
        )}
      </div>

      {/* Contract Detail Dialog */}
      <Dialog open={!!selectedContract} onOpenChange={() => setSelectedContract(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Détails du Contrat #{selectedContract?.contract_number}</DialogTitle>
          </DialogHeader>
          
          {selectedContract && (
            <Tabs defaultValue="info" className="mt-4">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="info">Informations</TabsTrigger>
                <TabsTrigger value="service">Service</TabsTrigger>
                <TabsTrigger value="payment">Paiement</TabsTrigger>
              </TabsList>

              <TabsContent value="info" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-500">Client</Label>
                    <p className="font-medium">
                      {selectedContract.client_info?.first_name} {selectedContract.client_info?.last_name}
                    </p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Email</Label>
                    <p className="font-medium">{selectedContract.client_info?.email}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Téléphone</Label>
                    <p className="font-medium">{selectedContract.client_info?.phone}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Adresse</Label>
                    <p className="font-medium">
                      {selectedContract.client_info?.address}, {selectedContract.client_info?.postal_code} {selectedContract.client_info?.city}
                    </p>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="service" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-500">Service</Label>
                    <p className="font-medium">{selectedContract.service_info?.service_name}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Type d'événement</Label>
                    <p className="font-medium">{selectedContract.service_info?.event_type}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Date</Label>
                    <p className="font-medium">{formatDate(selectedContract.service_info?.event_date)}</p>
                  </div>
                  <div>
                    <Label className="text-slate-500">Durée</Label>
                    <p className="font-medium">{selectedContract.service_info?.duration_hours}h</p>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-slate-500">Lieu</Label>
                    <p className="font-medium">{selectedContract.service_info?.event_location}</p>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="payment" className="space-y-4 mt-4">
                <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Prix de base</span>
                    <span className="font-medium">{formatCurrency(selectedContract.financials?.base_price)}</span>
                  </div>
                  {selectedContract.financials?.options_price > 0 && (
                    <div className="flex justify-between">
                      <span className="text-slate-600">Options</span>
                      <span className="font-medium">{formatCurrency(selectedContract.financials?.options_price)}</span>
                    </div>
                  )}
                  {selectedContract.financials?.delivery_fee > 0 && (
                    <div className="flex justify-between">
                      <span className="text-slate-600">Frais de livraison</span>
                      <span className="font-medium">{formatCurrency(selectedContract.financials?.delivery_fee)}</span>
                    </div>
                  )}
                  <div className="border-t pt-3 flex justify-between">
                    <span className="text-slate-600">Total HT</span>
                    <span className="font-medium">{formatCurrency(selectedContract.financials?.total_ht)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">TVA ({selectedContract.financials?.tva_rate}%)</span>
                    <span className="font-medium">{formatCurrency(selectedContract.financials?.tva_amount)}</span>
                  </div>
                  <div className="border-t pt-3 flex justify-between text-lg font-semibold">
                    <span>Total TTC</span>
                    <span className="text-indigo-600">{formatCurrency(selectedContract.financials?.total_ttc)}</span>
                  </div>
                  <div className="border-t pt-3">
                    <div className="flex justify-between text-amber-700">
                      <span>Acompte ({selectedContract.financials?.deposit_percentage}%)</span>
                      <span className="font-medium">{formatCurrency(selectedContract.financials?.deposit_amount)}</span>
                    </div>
                    <div className="flex justify-between mt-1">
                      <span className="text-slate-600">Solde à payer</span>
                      <span className="font-medium">{formatCurrency(selectedContract.financials?.balance_amount)}</span>
                    </div>
                    <div className="flex justify-between mt-1 text-slate-500">
                      <span>Caution</span>
                      <span>{formatCurrency(selectedContract.financials?.caution_amount)}</span>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
